
import checkForErrors from '@/helpers/checkForErrors';
import conversationAPI from '@/api/conversations';
import hasClientLoginCheck from '@/mixins/hasClientLoginCheck';
import hasCustomFields from '@/mixins/hasCustomFields';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'new-conversation',
  mixins: [hasCustomFields, hasClientLoginCheck],

  props: {
    depth: {
      type: String,
      required: true,
    },
    depth_uuid: {
      type: String | null,
      required: true,
    },
    participants: {
      type: Array | null,
      required: false,
    },
  },

  data() {
    return {
      modelType: 'conversation',
      newConvo: {
        name: '',
        participants: [],
        message: '',
        files: [],
      },
      accountParticipants: [],
      newClientsWithoutLogins: [],
      client: {},
    };
  },

  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('user', ['currentAccountUuid', 'isEmployee']),

    accountSelectorType() {
      if (this.depth === 'employee') {
        return 'employee';
      }
      if (this.depth === 'external') {
        return 'external';
      } else {
        return 'client';
      }
    },

    mentionItems() {
      const participants = Array.isArray(this.accountParticipants) ? this.accountParticipants : [];
      const items = {};
      participants.forEach((participant) => {
        if (participant && participant.uuid && participant.name) {
          items[participant.uuid] = {
            item: participant.name,
            replacement: `<span data-type="mention" data-account-uuid="${participant.uuid}">@${participant.name}</span>`,
          };
        }
      });
      return items;
    },
  },

  created() {
    this.newConvo = this.buildDataBlob(this.newConvo);
  },

  mounted() {
    // if we're already at client / case level then we want to merge incoming participants with the current depth
    if (this.isEmployee) {
      let people = [];
      if (this.participants && this.participants.length) {
        people = this.participants;
      }

      this.accountParticipants = people;
    } else {
      this.accountParticipants = null;
    }

    this.$nuxt.$on('handleFiles', this.handleFiles);
  },

  beforeDestroy() {
    this.$nuxt.$off('handleFiles', this.handleFiles);
  },

  methods: {
    closeModal() {
      this.$emit('close');
    },

    async createConversation() {
      const errors = this.validateForm();
      if (errors.length) {
        this.$refs.formErrorPanel.setErrors(errors);
        return;
      }

      if (this.depth === 'case' && this.depth_uuid) {
        this.newConvo = { ...this.newConvo, case: this.depth_uuid };
      }

      const payload = { ...this.newConvo };

      if (payload.participants.length === 0) {
        delete payload.participants;
      }

      const messageHtml = payload.message || '';
      const messageHasText = messageHtml.replace(/<[^>]*>/g, '').trim() !== '';
      const messageHasMention = messageHtml.includes('data-type="mention"');
      if (!messageHasText && !messageHasMention) {
        delete payload.message;
      } else {
        payload.message = messageHtml;
      }

      if (payload.files && payload.files.length > 0) {
        payload.files = payload.files.map((file) => file.uuid);
      } else {
        delete payload.files;
      }

      try {
        const rsp = await conversationAPI.createConversation(payload);
        this.$nuxt.$emit('refreshConversations');
        this.newConvo = {
          name: '',
          participants: this.participants,
          message: '',
          files: [],
        };
        this.$nuxt.$emit('viewConvo', rsp.data.data);
      } catch (err) {
        if (err.response.status === 403) {
          const message = err.response.data.message;
          this.$nuxt.$emit('confirm', {
            hideNoButton: true,
            message: message,
          });
        }
        checkForErrors.process(err, this.$refs.formErrorPanel);
      }
    },

    handleFiles(files, uploadTarget) {
      if (uploadTarget !== 'conversation-new') {
        return;
      }

      this.newConvo.files = this.newConvo.files.concat(files);
    },

    validateForm() {
      const errors = [];

      if (!this.newConvo.name || this.newConvo.name.trim() === '') {
        errors.push({ name: 'Please enter a title for this conversation.' });
      }

      return errors;
    },
  },

  watch: {
    accountParticipants: {
      handler: async function (new_val) {
        if (new_val !== null) {
          this.newConvo.participants = new_val.map((v) => v.uuid);
          this.newClientsWithoutLogins = await this.getClientsWithoutLogins(new_val);
        } else {
          this.newClientsWithoutLogins = [];
        }
      },
    },
  },
};
